import { Component, signal, OnInit, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Subject} from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { AuthenticationResult, RedirectRequest } from '@azure/msal-browser';
import { environment } from '../../environments/environment';
import { UserMeResponse, TokenResponse } from '../interfaces/api-responses';
import { SessionService } from '../core/session.service';
import { PassportsDataService } from '../shared/passports-data.service';
import { PersonaRoutingService } from '../shared/persona-routing.service';
import { PassportService } from '../shared/passport.service';
import { PassportDetailsStore } from '../shared/passport-details.store';

@Component({
  selector: 'app-sso-auth',
  standalone: true,
  imports: [FormsModule, CommonModule],
  templateUrl: './sso-auth.component.html',
  styleUrl: './sso-auth.component.css'
})
export class SsoAuthComponent implements OnInit, OnDestroy {
  step = signal<'start' | 'authenticating' | 'verifying'>('start');
  error = signal('');
  private readonly destroy$ = new Subject<void>();
  // Email extracted from ID token claims
  email = signal<string>('');

  constructor(
    private readonly http: HttpClient, 
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly msalService: MsalService,
    private readonly msalBroadcastService: MsalBroadcastService,
    private readonly session: SessionService,
    private readonly passportsData: PassportsDataService,
    private readonly personaRouting: PersonaRoutingService,
    private readonly passportService: PassportService,
    private readonly passportDetailsStore: PassportDetailsStore
  ) {}

  async startSso(): Promise<void> {
    this.error.set('');
    this.step.set('authenticating');
    
    try {
      // DEBUG: Log redirect URI values to troubleshoot redirect issues
      console.log('🔍 DEBUG - environment.redirectUri:', environment.redirectUri);
      console.log('🔍 DEBUG - globalThis.location.origin:', globalThis.location.origin);
      console.log('🔍 DEBUG - Using redirectUri directly (no fallback):', environment.redirectUri);
      
      // Configure redirect request
      // Use 'select_account' prompt to show account picker
      const loginRequest: RedirectRequest = {
        scopes: ['openid', 'profile', 'email'],
        redirectUri: environment.redirectUri,
        prompt: 'select_account' // Allow user to select account from picker
      };
      
      console.log('🔐 Starting SSO with account selection');
      
      // Store state to handle redirect after authentication
      sessionStorage.setItem('ssoAuthInProgress', 'true');
      
      // Initiate Azure AD login using redirect (more reliable than popup)
      // Note: loginRedirect returns void and redirects the page
      this.msalService.loginRedirect(loginRequest);
      
    } catch (error) {
      console.warn('SSO login redirect failed:', error);
      this.error.set('Authentication failed. Please try again.');
      this.step.set('start');
      
      // Fallback to login page after delay
      setTimeout(() => {
        this.router.navigateByUrl('/login');
      }, 3000);
    }
  }

  ngOnInit(): void {
    // Check if we're returning from a redirect authentication
    const authInProgress = sessionStorage.getItem('ssoAuthInProgress');
    
    // Handle redirect promise
    this.msalService.handleRedirectObservable().subscribe({
      next: (result) => {
        if (result?.account) {
          // Clear auth in progress flag
          sessionStorage.removeItem('ssoAuthInProgress');
          // DON'T remove ssoEmail here - we need it in handleAuthenticationSuccess
          
          // Authentication successful
          this.step.set('verifying');
          void this.handleAuthenticationSuccess(result);
        } else if (authInProgress === 'true') {
          // We were expecting auth but didn't get a result
          sessionStorage.removeItem('ssoAuthInProgress');
          this.checkExistingAuth();
        }
      },
      error: (error) => {
        console.warn('Redirect error:', error);
        sessionStorage.removeItem('ssoAuthInProgress');
        localStorage.removeItem('ssoEmail');
        this.error.set('Authentication failed. Redirecting to login...');
        this.step.set('authenticating');
        
        setTimeout(() => {
          this.router.navigateByUrl('/login');
        }, 2000);
      }
    });
    
    // If not returning from redirect, check existing auth or start SSO
    if (authInProgress !== 'true') {
      this.checkExistingAuthOrStartSSO();
    }
  }

  private checkExistingAuth(): void {
    const accounts = this.msalService.instance.getAllAccounts();
    
    if (accounts.length > 0) {
      // User is already authenticated, try to get token silently
      this.step.set('verifying');
      
      this.msalService.acquireTokenSilent({
        scopes: ['openid', 'profile', 'email'],
        account: accounts[0]
      }).subscribe({
        next: (result) => {
          void this.handleAuthenticationSuccess(result);
        },
        error: (error) => {
          console.warn('Silent token acquisition failed:', error);
          // Auto-trigger SSO login on failure
          this.startSso();
        }
      });
    } else {
      // No accounts found, show start screen
      this.step.set('start');
    }
  }

  private checkExistingAuthOrStartSSO(): void {
    const accounts = this.msalService.instance.getAllAccounts();
    
    if (accounts.length > 0) {
      // User is already authenticated, try to get token silently
      this.step.set('verifying');
      
      this.msalService.acquireTokenSilent({
        scopes: ['openid', 'profile', 'email'],
        account: accounts[0]
      }).subscribe({
        next: (result) => {
          void this.handleAuthenticationSuccess(result);
        },
        error: (error) => {
          console.warn('Silent token acquisition failed:', error);
          // Auto-trigger SSO login on failure
          setTimeout(() => this.startSso(), 500);
        }
      });
    } else {
      // No accounts found, automatically start SSO with a brief delay
      // This allows the loading UI to briefly show before redirect
      setTimeout(() => this.startSso(), 500);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async handleAuthenticationSuccess(result: AuthenticationResult): Promise<void> {
    this.step.set('verifying');
    
    try {
      // Log the full result object to see what Microsoft returned
      console.log('🔍 Full Microsoft AuthenticationResult:', {
        account: result.account ? {
          username: result.account.username,
          name: result.account.name,
          homeAccountId: result.account.homeAccountId,
          localAccountId: result.account.localAccountId,
          environment: result.account.environment,
          tenantId: result.account.tenantId,
          allProperties: result.account
        } : null,
        idTokenClaims: result.idTokenClaims,
        accessToken: result.accessToken ? '***REDACTED***' : null,
        expiresOn: result.expiresOn,
        tenantId: result.tenantId,
        uniqueId: result.uniqueId,
        scopes: result.scopes
      });
      
      // Log all claims separately for easier inspection
      console.log('📋 All ID Token Claims:', result.idTokenClaims);
      console.log('📋 Account Object Properties:', result.account ? Object.keys(result.account) : 'No account');
      if (result.account) {
        console.log('📋 Account Object Full:', JSON.stringify(result.account, null, 2));
      }
      
      // Get email from Microsoft authentication result
      let email = '';
      if (result.account) {
        email = result.account.username || '';
        console.log('📧 Email from Microsoft account.username:', email);
      }
      
      if (!email) {
        email = this.extractEmailFromClaims(result.idTokenClaims);
        console.log('📧 Email from Microsoft claims (extracted):', email);
      }
      
      if (!email) {
        throw new Error('No email found in authentication result. Please try logging in again.');
      }
      
      console.log('🔐 Authenticating user with email:', email);
      this.email.set(String(email));
      try { localStorage.setItem('email', String(email)); } catch {
        // Ignore localStorage errors (e.g., in private browsing mode)
      }
      
      // IMPORTANT: Clear old session data before authenticating new user
      // This ensures we don't show data from the previous user
      console.log('🧹 Clearing previous user session data...');
      this.clearPreviousUserData();
      
      // ✅ Pass ID token (not access token) to backend for validation
      // idToken is for app authentication, accessToken is for Microsoft Graph API
      // Also pass the account to ensure token refresh uses the correct account
      const authRes = await this.exchangeEmailForToken(result.idToken, result.account);
      console.log('✅ Token received for user:', authRes.username || email);
      
      // Store token and verify it was saved successfully
      const tokenStored = await this.storeAuthenticationToken(authRes);
      if (!tokenStored) {
        throw new Error('Failed to store authentication token. Please check your browser settings and try again.');
      }

      const userDetails = await this.fetchUserDetails(authRes);
      console.log('✅ User details retrieved:', { 
        userID: userDetails.userID, 
        username: userDetails.username,
        roles: userDetails.roles 
      });
      
      const persona = this.determinePersonaFromRoles(userDetails.roles);
      
      // Store user details and verify success
      const userDetailsStored = await this.storeUserDetails(userDetails, persona);
      if (!userDetailsStored) {
        throw new Error('Failed to store user details. Please check your browser settings and try again.');
      }
      
      this.updateSession(persona);
      
      await this.preloadPassports(persona);
      await this.navigateToPersonaRoute(persona);
      
    } catch (error) {
      console.warn('Failed to get user details:', error);
      this.error.set('Failed to retrieve user information. Please try again.');
      this.step.set('start');
    }
  }

  /**
   * Clear all previous user data before authenticating a new user
   * This prevents showing data from the previous user's session
   */
  private clearPreviousUserData(): void {
    try {
      // Clear session service
      this.session.clear();
      
      // Clear passport service records
      this.passportService.records.set([]);
      
      // Clear passport details store
      this.passportDetailsStore.clear();
      
      // Clear any cached data in localStorage (keep email for now)
      const email = localStorage.getItem('email');
      
      // Clear all auth-related localStorage items
      localStorage.removeItem('accessToken');
      localStorage.removeItem('tokenType');
      localStorage.removeItem('userID');
      localStorage.removeItem('employeeName');
      localStorage.removeItem('username');
      localStorage.removeItem('persona');
      localStorage.removeItem('roles');
      
      // Restore email if it exists
      if (email) {
        localStorage.setItem('email', email);
      }
      
      console.log('✅ Previous user data cleared');
    } catch (error) {
      console.warn('❌ Error clearing previous user data:', error);
      // Continue anyway - don't block authentication
    }
  }

  private extractEmailFromClaims(claims: any): string {
    const claimsObj = claims || {};
    
    // Log all available claims for debugging
    console.log('📋 Available Microsoft claims:', {
      email: claimsObj?.email,
      emails: claimsObj?.emails,
      preferred_username: claimsObj?.preferred_username,
      upn: claimsObj?.upn,
      unique_name: claimsObj?.unique_name,
      name: claimsObj?.name,
      allKeys: Object.keys(claimsObj || {})
    });
    
    // Priority order for extracting email:
    // 1. 'email' claim (most reliable for the authenticated user)
    // 2. 'upn' (User Principal Name - usually contains email)
    // 3. 'emails' array first element
    // 4. 'preferred_username' (may be the device account, not the authenticated user)
    // 5. 'unique_name' as last resort
    
    const extractedEmail = claimsObj?.email 
      ?? claimsObj?.upn 
      ?? (Array.isArray(claimsObj?.emails) ? claimsObj.emails[0] : undefined)
      ?? claimsObj?.preferred_username 
      ?? claimsObj?.unique_name
      ?? '';
    
    console.log('✅ Extracted email from claims:', extractedEmail);
    
    return extractedEmail;
  }

  /**
   * Get a fresh MSAL ID token, refreshing if necessary
   * Returns the ID token from the specified account or refreshes it silently
   * @param account - The account to use for token acquisition. If not provided, uses the first available account.
   */
  private async getFreshIdToken(account?: any): Promise<string> {
    let accountToUse = account;
    
    // If no account provided, try to get from available accounts
    if (!accountToUse) {
      const accounts = this.msalService.instance.getAllAccounts();
      
      if (accounts.length === 0) {
        throw new Error('No authenticated account found. Please login again.');
      }
      
      accountToUse = accounts[0];
      console.log('⚠️ No account specified, using first available account');
    }

    try {
      // Try to acquire token silently (will refresh if expired)
      // This ensures we get a token for the SPECIFIC account that was authenticated
      const result = await firstValueFrom(
        this.msalService.acquireTokenSilent({
          scopes: ['openid', 'profile', 'email'],
          account: accountToUse
        })
      );

      if (!result.idToken) {
        throw new Error('ID token not found in authentication result');
      }

      console.log('✅ Fresh MSAL ID token acquired for account:', accountToUse.username);
      return result.idToken;
    } catch (error) {
      console.warn('❌ Failed to acquire fresh ID token:', error);
      throw new Error('Failed to refresh authentication token. Please login again.');
    }
  }

  private async exchangeEmailForToken(idToken?: string, account?: any): Promise<TokenResponse> {
    const email = this.email();
    const authUrl = `${environment.apiBaseUrl}/api/v1/auth/token`;
    
    // Get fresh ID token if not provided or refresh if expired
    let msalToken = idToken;
    if (msalToken) {
      // Check if token is expired or about to expire (within 5 minutes)
      try {
        const tokenParts = msalToken.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          const exp = payload.exp * 1000; // Convert to milliseconds
          const now = Date.now();
          const fiveMinutes = 5 * 60 * 1000;
          
          if (exp - now < fiveMinutes) {
            console.log('🔄 ID token expires soon, refreshing...');
            msalToken = await this.getFreshIdToken(account);
          }
        }
      } catch (error) {
        console.warn('⚠️ Could not parse token expiration, refreshing anyway:', error);
        msalToken = await this.getFreshIdToken(account);
      }
    } else {
      console.log('🔄 No ID token provided, acquiring fresh token...');
      msalToken = await this.getFreshIdToken(account);
    }
    
    // ✅ Send ONLY MSAL ID token (not username)
    // Backend extracts email from the validated token
    const authBody = { msalToken: msalToken };
    const authHeaders = { 'Content-Type': 'application/json' };
    
    console.log('🔄 Exchanging MSAL ID token for custom JWT:', { 
      email, 
      authUrl, 
      sendingFields: Object.keys(authBody)  // Should only show ["msalToken"]
    });
    
    const tokenResponse = await firstValueFrom(
      this.http.post<TokenResponse>(authUrl, authBody, { headers: authHeaders })
    );
    
    console.log('✅ Token exchange successful:', { 
      username: tokenResponse.username, 
      tokenType: tokenResponse.tokenType,
      hasToken: !!tokenResponse.accessToken 
    });
    
    // Verify the token is for the correct user
    if (tokenResponse.username && tokenResponse.username.toLowerCase() !== email.toLowerCase()) {
      console.warn('⚠️ Token username mismatch!', {
        expected: email,
        received: tokenResponse.username
      });
    }
    
    return tokenResponse;
  }

  private async storeAuthenticationToken(authRes: TokenResponse): Promise<boolean> {
    try {
      console.log('💾 Storing authentication token...', {
        hasToken: !!authRes.accessToken,
        tokenLength: authRes.accessToken?.length || 0,
        tokenType: authRes.tokenType,
        username: authRes.username
      });
      
      localStorage.setItem('accessToken', authRes.accessToken);
      localStorage.setItem('tokenType', authRes.tokenType);
      localStorage.setItem('username', authRes.username || this.email());
      
      console.log('✅ Token stored successfully in localStorage');
      
      // Verify storage - CRITICAL: Must succeed or authentication will fail!
      const storedToken = localStorage.getItem('accessToken');
      if (!storedToken) {
        console.warn('❌ CRITICAL: Token was not stored! localStorage.getItem returned null');
        console.warn('This will cause API calls to fail. Possible causes:');
        console.warn('- Private/Incognito browsing mode');
        console.warn('- Browser privacy settings blocking storage');
        console.warn('- Content Security Policy restrictions');
        this.error.set('❌ Failed to save authentication token. Please check your browser settings (disable private browsing, allow cookies/storage for this site).');
        return false;
      }
      
      if (storedToken !== authRes.accessToken) {
        console.warn('❌ CRITICAL: Token mismatch! Stored token differs from original');
        this.error.set('❌ Token verification failed. Please try again.');
        return false;
      }
      
      console.log('✅ Token verified in localStorage');
      return true;
      
    } catch (error) {
      console.warn('❌ CRITICAL: Failed to store authentication token in localStorage!', error);
      console.warn('localStorage available?', typeof localStorage !== 'undefined');
      console.warn('localStorage quota:', this.getLocalStorageQuota());
      console.warn('Error details:', error);
      
      // Alert user that authentication cannot proceed
      this.error.set(`❌ Unable to save session data: ${error instanceof Error ? error.message : 'Unknown error'}. Please check your browser settings.`);
      return false;
    }
  }
  
  /**
   * Get localStorage quota information for debugging
   */
  private getLocalStorageQuota(): string {
    try {
      let total = 0;
      for (const key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
          total += localStorage[key].length + key.length;
        }
      }
      return `${(total / 1024).toFixed(2)} KB used`;
    } catch {
      return 'Unable to determine';
    }
  }

  private async fetchUserDetails(authRes: TokenResponse): Promise<UserMeResponse> {
    const meUrl = `${environment.apiBaseUrl}/api/v1/auth/me`;
    const headers = {
      'Authorization': `${authRes.tokenType} ${authRes.accessToken}`
    };

    return await firstValueFrom(
      this.http.get<UserMeResponse>(meUrl, { headers })
    );
  }

  private determinePersonaFromRoles(roles: string[] | undefined): string {
    if (!roles || roles.length === 0) {
      return '';
    }
    
    if (roles.includes('Grower')) {
      return 'Grower';
    }
    
    return roles[0];
  }

  private async storeUserDetails(userDetails: UserMeResponse, persona: string): Promise<boolean> {
    try {
      console.log('💾 Storing user details...', {
        userID: userDetails.userID,
        username: userDetails.username,
        persona,
        rolesCount: userDetails.roles?.length || 0
      });
      
      localStorage.setItem('userID', userDetails.userID.toString());
      localStorage.setItem('employeeName', userDetails.employeeName);
      localStorage.setItem('persona', persona);
      localStorage.setItem('roles', JSON.stringify(userDetails.roles || []));
      
      // Verify storage
      const storedUserID = localStorage.getItem('userID');
      if (!storedUserID || storedUserID !== userDetails.userID.toString()) {
        console.warn('❌ CRITICAL: User details were not stored correctly!');
        this.error.set('❌ Failed to save user details. Please check your browser settings.');
        return false;
      }
      
      console.log('✅ User details stored successfully in localStorage');
      return true;
      
    } catch (error) {
      console.warn('❌ CRITICAL: Failed to store user details in localStorage!', error);
      console.warn('This may cause issues with user session and permissions.');
      
      // Cannot proceed without user details
      this.error.set(`❌ Unable to save user details: ${error instanceof Error ? error.message : 'Unknown error'}. Please check your browser settings.`);
      return false;
    }
  }

  private updateSession(persona: string): void {
    this.session.refreshFromStorage();
    if (persona) {
      this.session.setPersona(persona);
    }
  }

  private async preloadPassports(persona: string): Promise<void> {
    try { 
      await this.passportsData.preloadForCurrentUser();
    } catch (error) {
      console.warn('Failed to preload passports:', error);
    }
  }

  private async navigateToPersonaRoute(persona: string): Promise<void> {
    const route = this.personaRouting.personaToRoute(persona);
    await this.router.navigateByUrl(route);
  }

}


